import { ActionType } from '../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../core/api/rsaa';
import { Endpoints } from '../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../core/api/module';
import { Rater } from '../../project/survey/raters/models';
import * as R from 'ramda';
import { toIndexAwareMap } from '../../language/arrays';
import { RaterApprovalStatus } from '../raterApprovalStatus';
import { AppState } from '../../core/redux/appState';


const KEY_ACTION = ActionType.DeclineRaterRequest;
const ACTIONS    = [
  ActionType.DeclineRaterRequest,
  ActionType.DeclineRaterResponse,
  ActionType.DeclineRaterError,
];


type Payload = Rater;


type Meta = {
  participantId: number;
  projectId: number;
  raterId: number;
};


/**
 * Dispatch this action to change a participant rater's approval status (i.e., to approve or decline the rater)
 *
 * @param participantId - database Id of the specific project participant for whom we wish to invite raters
 * @param projectId - database Id of the project to which we wish to invite raters
 * @param raterId - person Id for the rater whose approval status we wish to change
 */
export const declineRaterAction = (
  participantId: number,
  projectId: number,
  raterId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.UpdateApprovalStatus,
    headers:  { 'content-type': 'application/json' },
    body: (state: AppState) => JSON.stringify(
      {
        participantId,
        projectId,
        raterId,
        pageLanguageId: state.locale.lang.languageId,
        approvalStatus: RaterApprovalStatus.Declined,
      }),    types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { participantId, projectId, raterId }),
    bailout:  state => !!getLoading(state, KEY_ACTION, `${participantId}-${projectId}-${raterId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const participantId = action.meta.participantId;
  const projectId     = action.meta.projectId;
  const raterId       = action.meta.raterId;
  const metaTag       = `${participantId}-${projectId}-${raterId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const projectRaterArray = state.raterApprovalRaters?.[`${participantId}`]?.[`${projectId}`] || [];

  if (payload!.raterId === 0) {
    return R.assocPath(
      ['raterApprovalRaters', `${participantId}`, `${projectId}`],
      projectRaterArray,
      successState,
    );
  } else {
    // Maintain the sort order we had before we send the update approval status request
    const projectRaterMap = toIndexAwareMap(
      projectRaterArray,
      r => `${r.email}`,
    );

    const raterToUpdate = projectRaterMap[payload!.email];

    return R.assocPath(
      ['raterApprovalRaters', `${participantId}`, `${projectId}`],
      R.update(raterToUpdate.sourceIndex, payload, projectRaterArray),
      successState,
    );
  }
};

export const declineRaterReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
